import { Integration } from "../types/index.js";
import OnvoBase from "../base.js";

/**
 * Class representing operations related to integrations.
 * Extends the base Onvo class to provide integration-specific functionality.
 */
export class OnvoIntegrations extends OnvoBase {
  /**
   * Lists all available integrations.
   * @returns {Promise<Integration[]>} A promise that resolves to an array of integration objects.
   */
  list(): Promise<Integration[]> {
    return this._fetchBase("/api/integrations") as Promise<Integration[]>;
  }

  /**
   * Retrieves a specific integration by ID.
   * @param {string} id - The ID of the integration to retrieve.
   * @returns {Promise<Integration>} A promise that resolves to the integration object.
   */
  get(id: string): Promise<Integration> {
    return this._fetchBase(`/api/integrations/${id}`) as Promise<Integration>;
  }

  /**
   * Creates a new integration.
   * @param {Integration} data - The data for the new integration.
   * @returns {Promise<Integration>} A promise that resolves to the created integration object.
   */
  create(data: Omit<Integration, "id" | "team" | "created_at" | "updated_at">): Promise<Integration> {
    return this._fetchBase("/api/integrations", "POST", data) as Promise<Integration>;
  }

  /**
   * Updates an existing integration.
   * @param {string} id - The ID of the integration to update.
   * @param {Integration} data - The updated integration data.
   * @returns {Promise<Integration>} A promise that resolves to the updated integration object.
   */
  update(id: string, data: Integration): Promise<Integration> {
    return this._fetchBase(`/api/integrations/${id}`, "PUT", data) as Promise<Integration>;
  }

  /**
   * Deletes an integration.
   * @param {string} id - The ID of the integration to delete.
   * @returns {Promise<void>} A promise that resolves when the integration is deleted.
   */
  delete(id: string): Promise<void> {
    return this._fetchBase(`/api/integrations/${id}`, "DELETE") as Promise<void>;
  }


}
